import React, { useEffect, useState } from "react";
import {
  Menu, Zap, BookOpen, User, Activity, Brain, Dumbbell, Map, Shield, Settings
} from "lucide-react";

// IMPORTANT: Import your individual page components here
import AthleteDashboard from "./athletechecklist";
import CreateAccount from "./athletecreateaccount";
import MarketplacePage from "./athletemarketplace";
import ProfilePage from "./athleteprofile";
import TrainingPage from "./athleteswot";
import MetricsPage from "./athletescores";


export default function AthleteDocs() {
  const [activeTab, setActiveTab] = useState("onboarding");
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // 1. Define your Sidebar Items here
  const menuItems = [
    { id: "onboarding", label: "Getting Started", icon: BookOpen },
    { id: "create", label: "Create Account", icon: BookOpen },
    { id: "marketplace", label: "Marketplace", icon: Map },
    { id: "metrics", label: "Data & Metrics", icon: Activity },
    { id: "training", label: "SWOT Analysis", icon: Brain },
    { id: "profile", label: "Athlete Profile", icon: User },
  ];

  // 2. Logic to switch between imported components
  const renderActivePage = () => {
    switch (activeTab) {
      case "onboarding":
        return <AthleteDashboard onNavigate={setActiveTab} />;
      case "create": return <CreateAccount />;
      case "marketplace": return <MarketplacePage />;
      case "profile": return <ProfilePage />;
      case "training": return <TrainingPage />;
      case "metrics": return <MetricsPage />;
      default: return <AthleteDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans">

      {/* MOBILE HEADER */}
      <div className="md:hidden bg-white border-b border-gray-200 p-4 flex justify-between items-center sticky top-0 z-50">
        <a href="/" className="flex items-center gap-2 cursor-pointer group">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold transition-transform group-hover:scale-105">
            <Zap size={18} fill="currentColor" />
          </div>
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>
      </div>

      {/* SIDEBAR */}
      <aside className={`
        fixed md:relative z-40 w-72 h-full bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
      `}>
        <a href="/" className="flex items-center gap-2 cursor-pointer group pb-6">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold transition-transform group-hover:scale-105">
            <Zap size={18} fill="currentColor" />
          </div>
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        <nav className="px-4 py-6 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === item.id
                ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                }`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 md:p-16">

          {/* Dynamic Content is rendered here */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {renderActivePage()}
          </div>

          {/* Persistent Footer */}
          <footer className="mt-24 pt-8 border-t border-gray-200 text-gray-400 text-[10px] font-bold uppercase tracking-widest flex justify-between">
            <span>© 2026 Nuvio Sport</span>
            <div className="flex gap-4">
              <span className="text-blue-500 hover:underline cursor-pointer">Help Center</span>
              <span className="text-blue-500 hover:underline cursor-pointer">Privacy</span>
            </div>
          </footer>
        </div>
      </main>

      {/* MOBILE OVERLAY */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}